import { useQuery } from '@tanstack/react-query';
import { ProviderInfo, useAPI } from 'data/api';

const AUTHORIZATION_CODE = 'authorizationCode';
const AUTHORIZATION_CODE_PKCE = 'authorizationCodePKCE';

export type Providers = {
  [key: string]: ProviderInfo;
};

export const getIsScopeRequired = (provider: string, providers: Providers) => {
  const providerInfo: ProviderInfo = providers[provider];
  return providerInfo?.oauth2Opts?.explicitScopesRequired ?? false;
};

const isAuthCodeGrantType = (info: ProviderInfo) =>
  info.oauth2Opts?.grantType === AUTHORIZATION_CODE
  || info.oauth2Opts?.grantType === AUTHORIZATION_CODE_PKCE;

const supportsAnyAction = (info: ProviderInfo) => !!(
  info.support?.read || info.support?.write || info.support?.proxy || info.support?.subscribe
);

// Multi-step custom providers need a provider app only when they declare builder
// inputs (e.g. Microsoft's clientId/clientSecret).
export const isMultiStepCustomWithInputs = (info: ProviderInfo) =>
  info.customOpts?.multiStep === true
  && (info.customOpts?.providerInputs?.length ?? 0) > 0;

// A provider is offered for provider-app creation if it either uses the OAuth
// authorization-code grant or is a multi-step custom provider with builder inputs.
const needsProviderApp = (info: ProviderInfo) =>
  isAuthCodeGrantType(info) || isMultiStepCustomWithInputs(info);

// Prod: needs-provider-app + at least one action type supported — our readiness signal.
// Alpha: needs-provider-app only, so new connectors can be tested before support flags are set.
export const filterProviders = (providers: Providers, requireProductionReady: boolean): Providers =>
  Object.fromEntries(
    Object.entries(providers).filter(([, info]) =>
      needsProviderApp(info) && (!requireProductionReady || supportsAnyAction(info)),
    ),
  );

export const useProviders = () => {
  const getAPI = useAPI();

  return useQuery({
    queryKey: ['listProviders'],
    queryFn: async () => {
      const api = await getAPI();
      return api.providerApi.listProviders();
    },
  });
};
